import { useEffect, useState } from 'react'
import { App, Card, Col, Row, Space, Statistic, Tag } from 'antd'
import { useQuery } from '@tanstack/react-query'
import dayjs from 'dayjs'

import {
  downloadMaintenanceReportCsv,
  maintenanceReportsApi,
  type ReportFilters,
} from '@/features/maintenance/api'
import DataTable from '@/shared/components/DataTable'
import PageHeader from '@/shared/components/PageHeader'
import { usePlantId } from '@/shared/hooks/usePlantId'
import ReportFilterBar from './components/ReportFilterBar'
import MaintenanceStatusBadge from './components/MaintenanceStatusBadge'
import { useMasterLookups } from './components/useMasterLookups'

interface Row {
  work_order_id: string
  work_order_no: string
  department_id: string | null
  machine_id: string | null
  priority: string
  status: string
  assigned_to: string | null
  reported_datetime: string
  ageing_hours: number
  waiting_for_spare: boolean
  spare_required: boolean
  safety_risk: boolean
  last_action_at: string | null
  ageing_bucket: string
  escalation_required: boolean
}

/** Spec §11 — Pending WO ageing with bucket distribution. */
export default function PendingWorkOrderAgeingReportPage() {
  const { message } = App.useApp()
  const { plantId } = usePlantId()
  const { machineCode, deptName } = useMasterLookups()
  const [filters, setFilters] = useState<ReportFilters>({ plant_id: plantId })
  useEffect(() => {
    if (filters.plant_id !== plantId) setFilters((f) => ({ ...f, plant_id: plantId }))
  }, [plantId, filters.plant_id])

  const { data, isLoading, refetch } = useQuery({
    queryKey: ['report-pending-ageing', filters],
    enabled: !!filters.plant_id,
    queryFn: () => maintenanceReportsApi.pendingAgeing(filters),
  })

  const rows: Row[] = (data?.rows ?? []) as unknown as Row[]
  const dist = data?.bucket_distribution ?? {}
  const escalated = rows.filter((r) => r.escalation_required).length

  const [exporting, setExporting] = useState(false)
  const exportCsv = async () => {
    setExporting(true)
    try {
      await downloadMaintenanceReportCsv(
        '/maintenance/reports/pending-ageing', filters,
        `pending-ageing-${dayjs().format('YYYYMMDD-HHmm')}.csv`,
      )
    } catch (e) { message.error((e as Error).message ?? 'Export failed') }
    finally { setExporting(false) }
  }

  const columns = [
    { title: 'WO', dataIndex: 'work_order_no', key: 'wo', width: 130 },
    {
      title: 'Machine', key: 'm', width: 140,
      render: (_: unknown, r: Row) => machineCode(r.machine_id),
    },
    {
      title: 'Dept', key: 'd', width: 140,
      render: (_: unknown, r: Row) => deptName(r.department_id),
    },
    {
      title: 'Priority', dataIndex: 'priority', key: 'p', width: 100,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Status', dataIndex: 'status', key: 's', width: 150,
      render: (v: string) => <MaintenanceStatusBadge status={v} />,
    },
    {
      title: 'Reported', dataIndex: 'reported_datetime', key: 'r', width: 140,
      render: (v: string) => dayjs(v).format('DD MMM HH:mm'),
    },
    { title: 'Ageing (h)', dataIndex: 'ageing_hours', key: 'a', width: 100 },
    { title: 'Bucket', dataIndex: 'ageing_bucket', key: 'b', width: 100,
      render: (v: string) => <Tag>{v}</Tag>,
    },
    {
      title: 'Flags', key: 'f', width: 200,
      render: (_: unknown, r: Row) => (
        <Space size={4} wrap>
          {r.waiting_for_spare && <Tag color="gold">SPARE</Tag>}
          {r.safety_risk && <Tag color="red">SAFETY</Tag>}
          {r.escalation_required && <Tag color="red">ESCALATE</Tag>}
        </Space>
      ),
    },
    {
      title: 'Last action', dataIndex: 'last_action_at', key: 'la', width: 140,
      render: (v: string | null) => v ? dayjs(v).format('DD MMM HH:mm') : '—',
    },
  ]

  return (
    <div>
      <PageHeader title="Pending Work Order Ageing"
        subtitle="Track unresolved jobs by ageing bucket. Escalation required is flagged automatically." />
      <ReportFilterBar
        filters={filters} onChange={setFilters} onRefresh={() => refetch()}
        onExport={exportCsv} exporting={exporting}
        show={{ department: true, priority: true, bucket: true, waitingSpareOnly: true }}
      />
      <Row gutter={12} style={{ marginBottom: 12 }}>
        <Col xs={12} md={6}><Card><Statistic title="Pending WOs" value={rows.length} /></Card></Col>
        <Col xs={12} md={6}><Card><Statistic title="Escalations" value={escalated} valueStyle={{ color: escalated > 0 ? '#cf1322' : undefined }} /></Card></Col>
        <Col xs={24} md={12}>
          <Card size="small" title="Bucket distribution">
            <Space wrap>
              {Object.entries(dist).map(([b, c]) => (
                <Card key={b} size="small" style={{ minWidth: 90 }}>
                  <Statistic title={b} value={c as number} />
                </Card>
              ))}
            </Space>
          </Card>
        </Col>
      </Row>
      <DataTable rowKey="work_order_id" columns={columns} dataSource={rows} loading={isLoading} />
    </div>
  )
}
